// (NO CHANGES TO EXISTING IMPORTS)
import React, { Suspense, useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
  useLocation,
} from "react-router-dom";
import MainLayout from "./layouts/MainLayout";
import { auth, db } from "./services/firebase";
import { getIdTokenResult, onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { buildTrialFields, getTrialStatus } from "./utils/trialStatus";
import { getAccessState } from "./utils/accessControl";
import FeatureGate from "./components/FeatureGate";
import {
  ensurePlatformOwnerClaim,
  isKnownPlatformOwnerUid,
} from "./utils/platformOwnerService";
import {
  startWorkspaceCloudSync,
  stopWorkspaceCloudSync,
} from "./utils/workspaceCloudSync";
import {
  startProjectBoardCloudSync,
  stopProjectBoardCloudSync,
} from "./utils/projectBoardStore";
import {
  startUserDataCloudSync,
  stopUserDataCloudSync,
} from "./utils/userDataCloudSync";
import {
  startUserPreferenceCloudSync,
  stopUserPreferenceCloudSync,
} from "./utils/userPreferenceCloudStore";
import {
  AI_CONVERSATIONS_COLLECTION,
  AI_CONVERSATIONS_STORAGE_KEY,
  AI_CONVERSATIONS_SYNC_EVENT,
} from "./utils/aiConversationStore";
import {
  CALCULATOR_RESULTS_COLLECTION,
  CALCULATOR_RESULTS_STORAGE_KEY,
  CALCULATOR_RESULTS_SYNC_EVENT,
} from "./utils/calculatorResultStore";
import { getAllJobs, mergeCloudJobs } from "./utils/jobStorage";
import {
  getRecords,
  getShopRecordTypes,
  mergeCloudRecords,
} from "./utils/shopDataStore";
import { logAppError } from "./services/appLogger";
import { identifyUser, trackPageView } from "./services/productAnalytics";
import { APP_ROUTE_PATHS } from "./appRoutePaths";
import {
  applyAvailableAppUpdate,
  checkForAvailableAppUpdate,
  deferAppUpdate,
  forceCacheRecoveryReload,
} from "./utils/appUpdateService";
import lazyWithChunkRecovery from "./utils/lazyWithChunkRecovery";
import "./App.css";
import "./pages/ShopPlatform.css";
import "./responsive.css";

const Calculator = lazyWithChunkRecovery(() => import("./pages/Calculator"));
const LandingPage = lazyWithChunkRecovery(() => import("./pages/LandingPage"));
const Auth = lazyWithChunkRecovery(() => import("./pages/Auth"));
const Dashboard = lazyWithChunkRecovery(() => import("./pages/Dashboard"));
const ProjectManagementSuite = lazyWithChunkRecovery(() => import("./pages/ProjectManagementSuite"));
const ProjectBoards = lazyWithChunkRecovery(() => import("./pages/ProjectBoards"));
const CalculationsSuite = lazyWithChunkRecovery(() => import("./pages/CalculationsSuite"));
const Settings = lazyWithChunkRecovery(() => import("./pages/Settings"));
const OnboardingPage = lazyWithChunkRecovery(() => import("./pages/OnboardingPage"));
const CompanyInvite = lazyWithChunkRecovery(() => import("./pages/CompanyInvite"));
const ProjectInvite = lazyWithChunkRecovery(() => import("./pages/ProjectInvite"));
const ReferencePage = lazyWithChunkRecovery(() => import("./pages/ReferencePage"));
const BillingPage = lazyWithChunkRecovery(() => import("./pages/BillingPage"));
const AdminDashboard = lazyWithChunkRecovery(() => import("./pages/Admin/AdminDashboard.js"));
const JobsDashboard = lazyWithChunkRecovery(() => import("./pages/jobs/JobsDashboard"));
const JobDetail = lazyWithChunkRecovery(() => import("./pages/jobs/JobDetail"));
const ProcessBuilder = lazyWithChunkRecovery(() => import("./pages/ProcessBuilder"));
const ToolDatabase = lazyWithChunkRecovery(() => import("./pages/ToolDatabase"));
const FixtureLibrary = lazyWithChunkRecovery(() => import("./pages/FixtureLibrary"));
const MachineDatabase = lazyWithChunkRecovery(() => import("./pages/MachineDatabase"));
const DrawingReviews = lazyWithChunkRecovery(() => import("./pages/DrawingReviews"));
const QuoteAnalyses = lazyWithChunkRecovery(() => import("./pages/QuoteAnalyses"));
const SetupSheets = lazyWithChunkRecovery(() => import("./pages/SetupSheets"));
const JobTravelers = lazyWithChunkRecovery(() => import("./pages/JobTravelers"));
const InspectionSheets = lazyWithChunkRecovery(() => import("./pages/InspectionSheets"));
const FirstArticleInspections = lazyWithChunkRecovery(() => import("./pages/FirstArticleInspections"));
const ControlPlans = lazyWithChunkRecovery(() => import("./pages/ControlPlans"));
const ShopBrain = lazyWithChunkRecovery(() => import("./pages/ShopBrain"));
const CapacityPlanner = lazyWithChunkRecovery(() => import("./pages/CapacityPlanner"));
const ShopFloorMode = lazyWithChunkRecovery(() => import("./pages/ShopFloorMode"));
const AIAssistant = lazyWithChunkRecovery(() => import("./pages/AIAssistant"));
const QRCodeCenter = lazyWithChunkRecovery(() => import("./pages/QRCodeCenter"));
const ExportCenter = lazyWithChunkRecovery(() => import("./pages/ExportCenter"));
const ThreeSPage = lazyWithChunkRecovery(() => import("./pages/ThreeSPage"));
const TrelloConnectPage = lazyWithChunkRecovery(() => import("./pages/TrelloConnectPage"));
const TrelloEmbeddedApp = lazyWithChunkRecovery(() => import("./pages/TrelloEmbeddedApp"));
const FeatureRequestPage = lazyWithChunkRecovery(() => import("./pages/FeatureRequestPage"));
const FeatureRequestsAdmin = lazyWithChunkRecovery(() => import("./pages/Admin/FeatureRequestsAdmin"));
const SystemHealthDashboard = lazyWithChunkRecovery(() => import("./pages/Admin/SystemHealthDashboard"));
const ProductAnalyticsDashboard = lazyWithChunkRecovery(() => import("./pages/Admin/ProductAnalyticsDashboard"));
const MessagesPage = lazyWithChunkRecovery(() => import("./pages/MessagesPage"));
const NotificationCenter = lazyWithChunkRecovery(() => import("./pages/NotificationCenter"));
const CollaborativeProjects = lazyWithChunkRecovery(() => import("./pages/CollaborativeProjects"));
const PersonalProjects = lazyWithChunkRecovery(() => import("./pages/PersonalProjects"));
const WorkspaceDashboard = lazyWithChunkRecovery(() => import("./pages/WorkspaceDashboard"));
const ProjectPage = lazyWithChunkRecovery(() => import("./pages/ProjectPage"));
const FileCenter = lazyWithChunkRecovery(() => import("./pages/FileCenter"));

const DEFAULT_PREFERENCES = {
  useImperial: true,
  learnMode: false,
  darkTheme: true,
};

const PROFILE_LOAD_TIMEOUT_MS = 8000;
const APP_BOOT_TIMEOUT_MS = 12000;

const withTimeout = (promise, timeoutMs, label = "operation") =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      window.setTimeout(() => reject(new Error(`${label} timed out after ${timeoutMs}ms`)), timeoutMs);
    }),
  ]);

const normalizeAccountType = (accountType, isFounder = false) => {
  if (isFounder) return "business";
  if (accountType === "company" || accountType === "business") return "business";
  return "personal";
};

const isBusinessAccountType = (accountType) =>
  accountType === "business" || accountType === "company";

const formatBuildDate = (buildInfo) => {
  if (!buildInfo?.buildTimestamp) return "new release";
  const date = new Date(buildInfo.buildTimestamp);
  if (Number.isNaN(date.getTime())) return "new release";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const PwaUpdateNotice = ({
  updateInfo,
  applyingUpdate,
  onApply,
  onLater,
  onRepair,
}) => {
  if (!updateInfo) return null;

  return (
    <section className="pwa-update-notice" role="status" aria-live="polite">
      <div className="pwa-update-copy">
        <span className="pwa-update-kicker">Update ready</span>
        <h2>A new ManuCalc release is available.</h2>
        <p>
          Version {updateInfo.remoteBuildInfo?.version || "current"} built {formatBuildDate(updateInfo.remoteBuildInfo)}
          {" "}is ready for this installed app. Save any open form edits, then update to load the newest tools and styling.
        </p>
      </div>
      <div className="pwa-update-actions">
        <button type="button" className="btn btn-primary" onClick={onApply} disabled={applyingUpdate}>
          {applyingUpdate ? "Updating..." : "Update now"}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onLater} disabled={applyingUpdate}>
          Later
        </button>
        <button type="button" className="btn btn-ghost" onClick={onRepair} disabled={applyingUpdate}>
          Repair cached files
        </button>
      </div>
    </section>
  );
};

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(previousProps) {
    if (this.state.hasError && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error, info) {
    console.error("ManuCalc route error:", error, info);
    logAppError({
      error,
      type: "route_error",
      module: "RouteErrorBoundary",
      userMessage: "This screen failed to load.",
      extra: { componentStack: info?.componentStack || "" },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="route-error-panel">
          <span>Route Error</span>
          <h2>This screen failed to load.</h2>
          <p>
            Refresh the page once. If it happens again, open the Project Management Suite
            or Calculations Suite and try again.
          </p>
          <div>
            <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
              Refresh
            </button>
            <a className="btn btn-secondary" href={APP_ROUTE_PATHS.calculationsSuite}>
              Back to Calculations Suite
            </a>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
};

function App() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkoutRedirecting, setCheckoutRedirecting] = useState(false);
  const [billingStateLoaded, setBillingStateLoaded] = useState(false);
  const [accountProfile, setAccountProfile] = useState(null);
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const [pwaUpdateInfo, setPwaUpdateInfo] = useState(null);
  const [applyingPwaUpdate, setApplyingPwaUpdate] = useState(false);


  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return undefined;

    const checkForAppUpdate = () => {
      checkForAvailableAppUpdate()
        .then((availableUpdate) => {
          if (availableUpdate) setPwaUpdateInfo(availableUpdate);
        })
        .catch((error) => {
        console.warn("ManuCalc app update check failed:", error);
      });
    };

    const startupTimer = window.setTimeout(checkForAppUpdate, 2500);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") checkForAppUpdate();
    };
    const handleFocus = () => checkForAppUpdate();
    const handleUpdateReady = (event) => {
      setPwaUpdateInfo((current) => ({
        ...(current || {}),
        registration: event.detail?.registration || current?.registration || null,
        hasWaitingWorker: true,
      }));
      checkForAppUpdate();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("manucalc:pwa-update-ready", handleUpdateReady);
    return () => {
      window.clearTimeout(startupTimer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("manucalc:pwa-update-ready", handleUpdateReady);
    };
  }, []);

  const handleApplyPwaUpdate = async () => {
    const shouldReload = window.confirm(
      "ManuCalc will reload once to apply the new release. Save any unsaved calculator, project, SOP, or setup-sheet edits before continuing. Update now?"
    );
    if (!shouldReload) return;

    setApplyingPwaUpdate(true);
    try {
      await applyAvailableAppUpdate(pwaUpdateInfo);
    } catch (error) {
      console.warn("ManuCalc app update apply failed:", error);
      setApplyingPwaUpdate(false);
    }
  };

  const handleDeferPwaUpdate = () => {
    deferAppUpdate(pwaUpdateInfo?.remoteBuildInfo);
    setPwaUpdateInfo(null);
  };

  const handleRepairPwaCache = async () => {
    const shouldRepair = window.confirm(
      "This clears ManuCalc cached app-shell files and reloads the app. Your account data stays in Firebase, but save open edits first. Continue?"
    );
    if (!shouldRepair) return;

    setApplyingPwaUpdate(true);
    try {
      await forceCacheRecoveryReload();
    } catch (error) {
      console.warn("ManuCalc cache repair failed:", error);
      setApplyingPwaUpdate(false);
    }
  };

  useEffect(() => {
    const authFallbackTimer = window.setTimeout(() => {
      setAuthReady((ready) => ready || true);
    }, 3500);

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      window.clearTimeout(authFallbackTimer);
      setUser(currentUser || null);
      if (currentUser) {
        identifyUser(currentUser, { signed_in: true });
        if (isKnownPlatformOwnerUid(currentUser.uid)) {
          setIsPlatformOwner(true);
        }
        getIdTokenResult(currentUser)
          .then(async (token) => {
            if (token.claims.platformOwner === true) {
              setIsPlatformOwner(true);
              return;
            }
            const activated = await ensurePlatformOwnerClaim(currentUser, token);
            setIsPlatformOwner(activated || isKnownPlatformOwnerUid(currentUser.uid));
          })
          .catch(() => setIsPlatformOwner(isKnownPlatformOwnerUid(currentUser.uid)));
      } else {
        setIsPlatformOwner(false);
      }
      setAuthReady(true);
    });

    return () => {
      window.clearTimeout(authFallbackTimer);
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (authReady && preferencesReady && billingStateLoaded) return undefined;

    const bootWatchdog = window.setTimeout(() => {
      logAppError({
        type: "app_boot_timeout",
        module: "App.bootWatchdog",
        userMessage: "ManuCalc took too long to finish startup checks.",
        extra: {
          authReady,
          preferencesReady,
          billingStateLoaded,
          checkoutRedirecting,
        },
      });
      setAuthReady(true);
      setPreferences((current) => current || DEFAULT_PREFERENCES);
      setPreferencesReady(true);
      setBillingStateLoaded(true);
    }, APP_BOOT_TIMEOUT_MS);

    return () => window.clearTimeout(bootWatchdog);
  }, [authReady, billingStateLoaded, checkoutRedirecting, preferencesReady]);

  useEffect(() => {
    if (!user?.uid || !accountProfile) {
      stopWorkspaceCloudSync();
      stopProjectBoardCloudSync();
      return undefined;
    }

    let disposed = false;
    let cleanup = () => {};
    let boardCleanup = () => {};

    Promise.all([
      startWorkspaceCloudSync({
        user,
        accountProfile,
        getLocalJobs: getAllJobs,
        mergeCloudJobs,
        getRecordTypes: getShopRecordTypes,
        getLocalRecords: getRecords,
        mergeCloudRecords,
      }),
      startProjectBoardCloudSync({
        user,
        accountProfile,
      }),
    ])
      .then(([stopWorkspace, stopBoards]) => {
        if (disposed) {
          stopWorkspace();
          stopBoards();
        } else {
          cleanup = stopWorkspace;
          boardCleanup = stopBoards;
        }
      })
      .catch((error) => {
        console.warn("Workspace cloud sync could not start:", error);
        logAppError({
          error,
          type: "sync_error",
          module: "workspaceCloudSync",
          userMessage: "Workspace cloud sync could not start.",
          accountType: accountProfile?.accountType || "",
        });
      });

    return () => {
      disposed = true;
      cleanup();
      boardCleanup();
    };
  }, [accountProfile, user]);

  useEffect(() => {
    if (!user?.uid) {
      stopUserDataCloudSync();
      stopUserPreferenceCloudSync();
      return undefined;
    }

    const stop = startUserDataCloudSync({
      user,
      stores: [
        {
          collectionName: AI_CONVERSATIONS_COLLECTION,
          storageKey: AI_CONVERSATIONS_STORAGE_KEY,
          eventName: AI_CONVERSATIONS_SYNC_EVENT,
          maxRecords: 250,
        },
        {
          collectionName: CALCULATOR_RESULTS_COLLECTION,
          storageKey: CALCULATOR_RESULTS_STORAGE_KEY,
          eventName: CALCULATOR_RESULTS_SYNC_EVENT,
          maxRecords: 80,
        },
        {
          collectionName: "savedCalculations",
          storageKey: "manucalc_calculations",
          eventName: "manucalc:legacy-calculations-updated",
          maxRecords: 250,
        },
      ],
    });
    const stopPreferences = startUserPreferenceCloudSync(user);

    return () => {
      stop();
      stopPreferences();
    };
  }, [user]);

  useEffect(() => {
    let mounted = true;

    async function loadUserData() {
      if (!authReady) return;

      if (!user?.uid) {
        if (mounted) {
          setPreferences(DEFAULT_PREFERENCES);
          setTrialStatus(null);
          setSubscriptionStatus(null);
          setPreferencesReady(true);
          setBillingStateLoaded(true);
          setCheckoutRedirecting(false);
          setAccountProfile(null);
        }
        return;
      }

      try {
        setPreferencesReady(false);
        setBillingStateLoaded(false);
        setAccountProfile(null);

        const userRef = doc(db, "users", user.uid);
        let userSnap = await withTimeout(
          getDoc(userRef),
          PROFILE_LOAD_TIMEOUT_MS,
          "User profile load"
        );

        if (!userSnap.exists()) {
          const now = Date.now();
          await withTimeout(
            setDoc(
              userRef,
              {
                email: user.email || "",
                username: user.displayName || String(user.email || "").split("@")[0],
                displayName: user.displayName || String(user.email || "").split("@")[0],
                accountType: "personal",
                accountBillingType: "personal",
                billingExempt: false,
                subscriptionRequired: true,
                role: "personal",
                ...buildTrialFields(now, "auto_profile_creation"),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            ),
            PROFILE_LOAD_TIMEOUT_MS,
            "User profile creation"
          );
          userSnap = await withTimeout(
            getDoc(userRef),
            PROFILE_LOAD_TIMEOUT_MS,
            "Created user profile load"
          );
        }

        let userData = userSnap.exists() ? userSnap.data() : null;

        if (mounted) {
          const hasLifetimeAdminIdentity =
            isKnownPlatformOwnerUid(user.uid) ||
            userData?.lifetimeAdminGrant === true ||
            userData?.accessSource === "lifetime_admin" ||
            userData?.accountBillingType === "lifetimeAdmin";
          const hasFounderIdentity =
            hasLifetimeAdminIdentity ||
            userData?.isFounder === true ||
            isPlatformOwner;

          if (
            hasFounderIdentity &&
            (userData?.accountType !== "business" ||
              userData?.accountBillingType !== (hasLifetimeAdminIdentity ? "lifetimeAdmin" : "founderLifetime") ||
              userData?.billingExempt !== true ||
              userData?.subscriptionRequired !== false ||
              userData?.isFounder !== true)
          ) {
            await withTimeout(
              setDoc(
                userRef,
                {
                  accountType: "business",
                  previousAccountType: userData?.accountType || "personal",
                  accountBillingType: hasLifetimeAdminIdentity ? "lifetimeAdmin" : "founderLifetime",
                  billingExempt: true,
                  subscriptionRequired: false,
                  subscriptionStatus: "active",
                  planTier: "business",
                  productTier: "business",
                  role: "owner",
                  activeWorkspaceId: user.uid,
                  companyId: user.uid,
                  companyOwnerUid: user.uid,
                  adminDashboardEnabled: true,
                  canExport: true,
                  isFounder: true,
                  lifetimeAdminGrant: hasLifetimeAdminIdentity,
                  founderAccessSource: hasLifetimeAdminIdentity ? "lifetime_admin" : isPlatformOwner ? "platform_owner" : "founder",
                  accessSource: hasLifetimeAdminIdentity ? "lifetime_admin" : "founder",
                  updatedAt: serverTimestamp(),
                },
                { merge: true }
              ),
              PROFILE_LOAD_TIMEOUT_MS,
              "Founder profile migration"
            );
            userData = {
              ...userData,
              accountType: "business",
              previousAccountType: userData?.accountType || "personal",
              accountBillingType: hasLifetimeAdminIdentity ? "lifetimeAdmin" : "founderLifetime",
              billingExempt: true,
              subscriptionRequired: false,
              subscriptionStatus: "active",
              planTier: "business",
              productTier: "business",
              role: "owner",
              activeWorkspaceId: user.uid,
              companyId: user.uid,
              companyOwnerUid: user.uid,
              adminDashboardEnabled: true,
              canExport: true,
              isFounder: true,
              lifetimeAdminGrant: hasLifetimeAdminIdentity,
              founderAccessSource: hasLifetimeAdminIdentity ? "lifetime_admin" : isPlatformOwner ? "platform_owner" : "founder",
              accessSource: hasLifetimeAdminIdentity ? "lifetime_admin" : "founder",
            };
          }
          const normalizedAccountType = normalizeAccountType(userData?.accountType, hasFounderIdentity);

          setPreferences({
            useImperial:
              userData?.preferences?.useImperial ??
              DEFAULT_PREFERENCES.useImperial,
            learnMode:
              userData?.preferences?.learnMode ??
              DEFAULT_PREFERENCES.learnMode,
            darkTheme: true,
          });

          const trial = getTrialStatus(
            userData?.trialStartedAt || userData?.trialStartDate,
            userData?.trialEndsAt || userData?.trialEndDate,
            userData?.trialStatus
          );

          setTrialStatus(trial);
          setSubscriptionStatus(userData?.subscriptionStatus || "none");
          setAccountProfile({
            accountType: normalizedAccountType,
            rawAccountType: userData?.accountType || "personal",
            accountBillingType: userData?.accountBillingType || "personal",
            billingExempt: Boolean(userData?.billingExempt),
            subscriptionRequired: userData?.subscriptionRequired !== false,
            subscriptionStatus: userData?.subscriptionStatus || "none",
            role: userData?.role || "personal",
            companyId: userData?.companyId || "",
            companyName: userData?.companyName || "",
            companyOwnerUid: userData?.companyOwnerUid || "",
            activeWorkspaceId: userData?.activeWorkspaceId || userData?.companyId || userData?.companyOwnerUid || "",
            seatCount: userData?.seatCount || 1,
            adminDashboardEnabled: Boolean(userData?.adminDashboardEnabled),
            canExport: Boolean(userData?.canExport),
            isFounder: Boolean(hasFounderIdentity),
            lifetimeAdminGrant: Boolean(hasLifetimeAdminIdentity),
            accessSource: hasLifetimeAdminIdentity ? "lifetime_admin" : userData?.accessSource || "",
            platformOwner: Boolean(isPlatformOwner || hasLifetimeAdminIdentity),
            founderNumber: userData?.founderNumber || null,
            founderGrantedAt: userData?.founderGrantedAt || null,
          });
          setBillingStateLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load user data:", error);
        logAppError({
          error,
          type: "firebase_error",
          module: "App.loadUserData",
          userMessage: "Your account profile could not be loaded.",
        });

        if (mounted) {
          setPreferences(DEFAULT_PREFERENCES);
          setTrialStatus(null);
          setSubscriptionStatus(null);
          setAccountProfile(null);
          setBillingStateLoaded(true);
        }
      } finally {
        if (mounted) {
          setPreferencesReady(true);
        }
      }
    }

    loadUserData();

    return () => {
      mounted = false;
    };
  }, [authReady, isPlatformOwner, user]);

  useEffect(() => {
    document.body.setAttribute("data-theme", "dark");

    return () => {
      document.body.removeAttribute("data-theme");
    };
  }, []);

  const handleAuth = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setPreferences(DEFAULT_PREFERENCES);
      setTrialStatus(null);
      setSubscriptionStatus(null);
      setCheckoutRedirecting(false);
      setPreferencesReady(true);
      setBillingStateLoaded(true);
      setAccountProfile(null);
      if (window.location.pathname !== APP_ROUTE_PATHS.auth) {
        window.location.assign(APP_ROUTE_PATHS.auth);
      }
    } catch (error) {
      console.error("Logout error:", error);
      logAppError({
        error,
        type: "auth_error",
        module: "App.handleLogout",
        userMessage: "Logout failed.",
      });
      window.alert("Logout failed. Please check your connection and try again.");
    }
  };

  const accessState = getAccessState({
    user,
    trialStatus,
    subscriptionStatus,
    accountProfile,
    isPlatformOwner,
  });

  const isSubscribed = accessState.isSubscribed;
  const isLifetimeAdmin = accessState.isLifetimeAdmin === true || accessState.accessSource === "lifetime_admin";

  const isCompanySeatAccount =
    accountProfile?.isFounder === true ||
    accountProfile?.accountBillingType === "companySeat" ||
    accountProfile?.billingExempt === true ||
    accountProfile?.subscriptionRequired === false ||
    (
      isBusinessAccountType(accountProfile?.accountType) &&
      accountProfile?.role !== "owner" &&
      accountProfile?.companyOwnerUid
    );

  const hasAccess = accessState.hasAccess || isCompanySeatAccount;

  const userRole = isLifetimeAdmin ? "owner" : accountProfile?.role || accessState.role || "personal";

  const isBusinessAdmin =
    accessState.canAccessAdmin === true ||
    (isBusinessAccountType(accountProfile?.accountType) &&
      userRole === "owner" &&
      accountProfile?.adminDashboardEnabled);

  const isCompanyOwner = isBusinessAdmin;

  const rolePermissions = {
    owner: {
      canUseAdmin: true,
      canUseProjects: true,
      canUseJobs: true,
      canUseCalculators: true,
      canUseSavedWork: true,
      canUseSettings: true,
      canExport: true,
      canManageShopData: true,
      canApproveRecords: true,
    },
    supervisor: {
      canUseAdmin: false,
      canUseProjects: true,
      canUseJobs: true,
      canUseCalculators: true,
      canUseSavedWork: true,
      canUseSettings: true,
      canExport: false,
      canManageShopData: true,
      canApproveRecords: true,
    },
    machinist: {
      canUseAdmin: false,
      canUseProjects: false,
      canUseJobs: true,
      canUseCalculators: true,
      canUseSavedWork: true,
      canUseSettings: true,
      canExport: false,
      canManageShopData: true,
      canApproveRecords: false,
    },
    estimator: {
      canUseAdmin: false,
      canUseProjects: true,
      canUseJobs: true,
      canUseCalculators: true,
      canUseSavedWork: true,
      canUseSettings: true,
      canExport: false,
      canManageShopData: true,
      canApproveRecords: false,
    },
    viewer: {
      canUseAdmin: false,
      canUseProjects: false,
      canUseJobs: true,
      canUseCalculators: true,
      canUseSavedWork: true,
      canUseSettings: true,
      canExport: false,
      canManageShopData: false,
      canApproveRecords: false,
    },
    personal: {
      canUseAdmin: false,
      canUseProjects: false,
      canUseJobs: false,
      canUseCalculators: true,
      canUseSavedWork: true,
      canUseSettings: true,
      canExport: true,
      canManageShopData: false,
      canApproveRecords: false,
    },
  };

  const basePermissions = isLifetimeAdmin ? rolePermissions.owner : rolePermissions[userRole] || rolePermissions.personal;
  const activePermissions = {
    ...basePermissions,
    canExport:
      basePermissions.canExport ||
      isCompanyOwner ||
      accountProfile?.canExport === true,
  };

  useEffect(() => {
    if (isSubscribed || isCompanySeatAccount || accessState.isFounder) {
      setCheckoutRedirecting(false);
    }
  }, [accessState.isFounder, isCompanySeatAccount, isSubscribed]);

  const renderProtectedPage = (content, options = {}) => {
    const {
      allowWithoutAccess = false,
      requireCompanyOwner = false,
      requirePlatformOwner = false,
      requiredPermission = "",
      feature = "",
    } = options;

    if (!user) return <Redirect to={APP_ROUTE_PATHS.auth} />;

    if (!isLifetimeAdmin && checkoutRedirecting) {
      return (
        <MainLayout
          user={user}
          onLogout={handleLogout}
          preferences={preferences}
          accountProfile={accountProfile}
          isCompanyOwner={isCompanyOwner}
          rolePermissions={activePermissions}
          isPlatformOwner={isPlatformOwner}
        >
          <div className="loading-screen">
            <div className="loading-spinner" aria-hidden="true" />
            <p>Redirecting to billing...</p>
          </div>
        </MainLayout>
      );
    }

    if (!hasAccess && !allowWithoutAccess) {
      return <Redirect to={APP_ROUTE_PATHS.billing} />;
    }

    if (requireCompanyOwner && !isCompanyOwner) {
      return <Redirect to={APP_ROUTE_PATHS.home} />;
    }

    if (requirePlatformOwner && !isPlatformOwner && !isLifetimeAdmin) {
      return <Redirect to={APP_ROUTE_PATHS.home} />;
    }

    if (requiredPermission && !activePermissions?.[requiredPermission]) {
      return <Redirect to={APP_ROUTE_PATHS.home} />;
    }

    const protectedContent = feature ? (
      <FeatureGate
        feature={feature}
        user={user}
        trialStatus={trialStatus}
        subscriptionStatus={subscriptionStatus}
        accountProfile={accountProfile}
      >
        {content}
      </FeatureGate>
    ) : (
      content
    );

    return (
      <MainLayout
        user={user}
        onLogout={handleLogout}
        preferences={preferences}
        accountProfile={accountProfile}
        isCompanyOwner={isCompanyOwner}
        rolePermissions={activePermissions}
        isPlatformOwner={isPlatformOwner}
      >
        <RouteErrorBoundary resetKey={`${window.location.pathname}${window.location.search}`}>
          <Suspense fallback={<div className="route-loading">Loading workspace...</div>}>
            {protectedContent}
          </Suspense>
        </RouteErrorBoundary>
      </MainLayout>
    );
  };

  const renderAppPage = (content) => (
    <MainLayout
      showFooter
      user={user}
      onLogout={handleLogout}
      preferences={preferences}
      accountProfile={accountProfile}
      isCompanyOwner={isCompanyOwner}
      rolePermissions={activePermissions}
      isPlatformOwner={isPlatformOwner}
    >
      {content}
    </MainLayout>
  );

  const renderEmbeddedPage = (content, options = {}) => {
    if (!user && !options.allowWithoutUser) {
      return <Redirect to={APP_ROUTE_PATHS.auth} />;
    }

    if (!hasAccess && !options.allowWithoutAccess) {
      return <Redirect to={APP_ROUTE_PATHS.billing} />;
    }

    const protectedContent = options.feature ? (
      <FeatureGate
        feature={options.feature}
        user={user}
        trialStatus={trialStatus}
        subscriptionStatus={subscriptionStatus}
        accountProfile={accountProfile}
      >
        {content}
      </FeatureGate>
    ) : (
      content
    );

    return (
      <RouteErrorBoundary resetKey={`embedded:${window.location.pathname}${window.location.search}`}>
        <Suspense fallback={<div className="route-loading">Loading ManuCalc workspace...</div>}>
          {protectedContent}
        </Suspense>
      </RouteErrorBoundary>
    );
  };

  if (!authReady || !preferencesReady || !billingStateLoaded || (checkoutRedirecting && !isLifetimeAdmin)) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" aria-hidden="true" />
        <p>
          {checkoutRedirecting && !isLifetimeAdmin
            ? "Redirecting to billing..."
            : "Loading ManuCalc..."}
        </p>
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <div
        className="App theme-dark"
      >
        <PwaUpdateNotice
          updateInfo={pwaUpdateInfo}
          applyingUpdate={applyingPwaUpdate}
          onApply={handleApplyPwaUpdate}
          onLater={handleDeferPwaUpdate}
          onRepair={handleRepairPwaCache}
        />
        <RouteErrorBoundary resetKey={`${window.location.pathname}${window.location.search}`}>
          <Suspense fallback={<div className="route-loading">Loading ManuCalc...</div>}>
            <Switch>
          <Route
            exact
            path={APP_ROUTE_PATHS.home}
            render={() =>
              user ? (
                hasAccess ? (
                  <Redirect to={APP_ROUTE_PATHS.dashboard} />
                ) : (
                  <MainLayout
                    showFooter
                    user={user}
                    onLogout={handleLogout}
                    preferences={preferences}
                    accountProfile={accountProfile}
                    isCompanyOwner={isCompanyOwner}
                    rolePermissions={activePermissions}
                    isPlatformOwner={isPlatformOwner}
                  >
                    <LandingPage
                      user={user}
                      preferences={preferences}
                      trialStatus={trialStatus}
                      subscriptionStatus={subscriptionStatus}
                      hasAccess={hasAccess}
                    />
                  </MainLayout>
                )
              ) : (
                <MainLayout
                  showFooter
                  user={user}
                  onLogout={handleLogout}
                  preferences={preferences}
                  accountProfile={accountProfile}
                  isCompanyOwner={isCompanyOwner}
                  rolePermissions={activePermissions}
                  isPlatformOwner={isPlatformOwner}
                >
                  <LandingPage
                    user={user}
                    preferences={preferences}
                    trialStatus={trialStatus}
                    subscriptionStatus={subscriptionStatus}
                    hasAccess={hasAccess}
                  />
                </MainLayout>
              )
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.auth}
            render={() =>
              user ? <Redirect to={APP_ROUTE_PATHS.dashboard} /> : <Auth onAuth={handleAuth} />
            }
          />

          <Route
            exact
            path="/calculators"
            render={({ location }) => (
              <Redirect
                to={{
                  pathname: APP_ROUTE_PATHS.calculationsSuite,
                  search: location.search,
                  hash: location.hash,
                }}
              />
            )}
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.companyInvite}
            render={() => <CompanyInvite user={user} />}
          />
          <Route
            exact
            path={APP_ROUTE_PATHS.projectInvite}
            render={() => <ProjectInvite user={user} />}
          />


          <Route
            exact
            path={APP_ROUTE_PATHS.dashboard}
            render={() =>
              renderProtectedPage(
                <Dashboard user={user} preferences={preferences} accountProfile={accountProfile} />
              )
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.projectManagement}
            render={() =>
              renderProtectedPage(
                <ProjectManagementSuite
                  user={user}
                  accountProfile={accountProfile}
                  rolePermissions={activePermissions}
                  isPlatformOwner={isPlatformOwner}
                />,
                { requiredPermission: "canUseCalculators", feature: "calculators" }
              )
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.projectBoards}
            render={() =>
              renderProtectedPage(
                <ProjectBoards user={user} accountProfile={accountProfile} />,
                { requiredPermission: "canUseCalculators", feature: "calculators" }
              )
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.calculationsSuite}
            render={() =>
              renderProtectedPage(
                <CalculationsSuite
                  user={user}
                  accountProfile={accountProfile}
                  preferences={preferences}
                />,
                { requiredPermission: "canUseCalculators", feature: "calculators" }
              )
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.personalProjects}
            render={() =>
              renderProtectedPage(<PersonalProjects user={user} />, {
                requiredPermission: "canUseCalculators",
                feature: "calculators",
              })
            }
          />

          <Route exact path={APP_ROUTE_PATHS.companyProject} render={({ match }) =>
            renderProtectedPage(
              <ProjectPage
                user={user}
                accountProfile={accountProfile}
                projectId={match.params.projectId}
              />,
              { requiredPermission: "canUseJobs", feature: "collaboration" }
            )
          } />

          <Route
            exact
            path={APP_ROUTE_PATHS.workspace}
            render={() =>
              renderProtectedPage(<WorkspaceDashboard user={user} accountProfile={accountProfile} />, {
                requiredPermission: "canUseJobs",
                feature: "manufacturingPlans",
              })
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.files}
            render={() =>
              renderProtectedPage(<FileCenter user={user} accountProfile={accountProfile} />, {
                requiredPermission: "canUseJobs",
                feature: "fileCenter",
              })
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.settings}
            render={() =>
              renderProtectedPage(
                <Settings
                  user={user}
                  preferences={preferences}
                  setPreferences={setPreferences}
                />,
                { allowWithoutAccess: true, requiredPermission: "canUseSettings" }
              )
            }
          />

          <Route exact path={APP_ROUTE_PATHS.onboarding} render={() =>
            renderProtectedPage(<OnboardingPage user={user} accountProfile={accountProfile} />, {
              allowWithoutAccess: true,
              requiredPermission: "canUseSettings",
            })
          } />

          <Route exact path={APP_ROUTE_PATHS.billing} render={() =>
            renderProtectedPage(<BillingPage user={user} accountProfile={accountProfile} />, {
              allowWithoutAccess: true,
              requiredPermission: "canUseSettings",
            })
          } />

          <Route exact path={APP_ROUTE_PATHS.billingSuccess} render={() =>
            renderProtectedPage(<BillingPage user={user} accountProfile={accountProfile} />, {
              allowWithoutAccess: true,
              requiredPermission: "canUseSettings",
            })
          } />

          <Route exact path={APP_ROUTE_PATHS.billingCancel} render={() =>
            renderProtectedPage(<BillingPage user={user} accountProfile={accountProfile} />, {
              allowWithoutAccess: true,
              requiredPermission: "canUseSettings",
            })
          } />

          <Route exact path={APP_ROUTE_PATHS.billingReturn} render={() =>
            renderProtectedPage(<BillingPage user={user} accountProfile={accountProfile} />, {
              allowWithoutAccess: true,
              requiredPermission: "canUseSettings",
            })
          } />

          <Route
            exact
            path={APP_ROUTE_PATHS.formulaReference}
            render={() => renderAppPage(<ReferencePage type="formulas" accountProfile={accountProfile} />)}
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.materialData}
            render={() => renderAppPage(<ReferencePage type="materials" accountProfile={accountProfile} />)}
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.resources}
            render={() => renderAppPage(<ReferencePage type="resources" accountProfile={accountProfile} />)}
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.whatsNew}
            render={() => renderAppPage(<ReferencePage type="whatsNew" accountProfile={accountProfile} />)}
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.about}
            render={() => renderAppPage(<ReferencePage type="about" accountProfile={accountProfile} />)}
          />

          <Route exact path={APP_ROUTE_PATHS.featureRequest} render={() =>
            renderProtectedPage(<FeatureRequestPage user={user} accountProfile={accountProfile} />, {
              requiredPermission: "canUseCalculators",
              feature: "calculators",
            })
          } />

          <Route exact path={APP_ROUTE_PATHS.platformFeatureRequests} render={() =>
            renderProtectedPage(<FeatureRequestsAdmin user={user} />, {
              allowWithoutAccess: true,
              requirePlatformOwner: true,
            })
          } />

          <Route exact path={APP_ROUTE_PATHS.ownerRequests} render={() =>
            renderProtectedPage(<FeatureRequestsAdmin user={user} />, {
              allowWithoutAccess: true,
              requirePlatformOwner: true,
            })
          } />

          <Route exact path={APP_ROUTE_PATHS.systemHealth} render={() =>
            renderProtectedPage(<SystemHealthDashboard user={user} />, {
              allowWithoutAccess: true,
              requirePlatformOwner: true,
            })
          } />

          <Route exact path={APP_ROUTE_PATHS.productAnalytics} render={() =>
            renderProtectedPage(<ProductAnalyticsDashboard user={user} />, {
              allowWithoutAccess: true,
              requirePlatformOwner: true,
            })
          } />

          <Route exact path={APP_ROUTE_PATHS.collaborativeProjects} render={() =>
            renderProtectedPage(<CollaborativeProjects user={user} accountProfile={accountProfile} />, {
              requiredPermission: "canUseProjects",
              feature: "collaboration",
            })
          } />

          <Route exact path={APP_ROUTE_PATHS.messages} render={() =>
            renderProtectedPage(<MessagesPage user={user} accountProfile={accountProfile} />, {
              requiredPermission: "canUseJobs",
              feature: "collaboration",
            })
          } />

          <Route exact path={APP_ROUTE_PATHS.notifications} render={() =>
            renderProtectedPage(<NotificationCenter user={user} accountProfile={accountProfile} />, {
              requiredPermission: "canUseJobs",
            })
          } />

          <Route
            exact
            path={APP_ROUTE_PATHS.savedJobs}
            render={() =>
              renderProtectedPage(
                <Redirect
                  to={
                    isBusinessAccountType(accountProfile?.accountType)
                      ? APP_ROUTE_PATHS.jobs
                      : APP_ROUTE_PATHS.personalProjects
                  }
                />,
                {
                  requiredPermission: "canUseSavedWork",
                }
              )
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.admin}
            render={() =>
              renderProtectedPage(
                <AdminDashboard
                  user={user}
                  preferences={preferences}
                  accountProfile={accountProfile}
                  canAccessAdmin={isBusinessAdmin || isLifetimeAdmin}
                />,
                { requiredPermission: "canUseAdmin", feature: "adminDashboard" }
              )
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.jobs}
            render={() =>
              renderProtectedPage(
                <JobsDashboard user={user} preferences={preferences} />,
                { requiredPermission: "canUseJobs", feature: "manufacturingPlans" }
              )
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.jobDetail}
            render={({ match }) =>
              renderProtectedPage(
                <JobDetail
                  user={user}
                  preferences={preferences}
                  accountProfile={accountProfile}
                  jobId={match.params.jobId}
                />,
                { requiredPermission: "canUseJobs", feature: "manufacturingPlans" }
              )
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.processBuilder}
            render={() =>
              renderProtectedPage(<ProcessBuilder />, {
                requiredPermission: "canUseJobs",
                feature: "shopRecords",
              })
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.processBuilderDetail}
            render={({ match }) =>
              renderProtectedPage(<ProcessBuilder jobId={match.params.jobId} />, {
                requiredPermission: "canUseJobs",
                feature: "shopRecords",
              })
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.tools}
            render={() =>
              renderProtectedPage(<ToolDatabase user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} />, {
                requiredPermission: "canManageShopData",
                feature: "shopRecords",
              })
            }
          />

          <Route exact path={APP_ROUTE_PATHS.fixtures} render={() =>
            renderProtectedPage(
              <FixtureLibrary user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} />,
              { requiredPermission: "canUseJobs", feature: "shopRecords" }
            )
          } />

          <Route exact path={APP_ROUTE_PATHS.machines} render={() =>
            renderProtectedPage(
              <MachineDatabase user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} />,
              { requiredPermission: "canUseJobs", feature: "shopRecords" }
            )
          } />

          <Route exact path={APP_ROUTE_PATHS.drawingReviews} render={() =>
            renderProtectedPage(
              <DrawingReviews user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} canApprove={activePermissions.canApproveRecords} />,
              { requiredPermission: "canUseJobs", feature: "shopRecords" }
            )
          } />

          <Route exact path={APP_ROUTE_PATHS.quoteAnalyses} render={() =>
            renderProtectedPage(
              <QuoteAnalyses user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} canApprove={activePermissions.canApproveRecords} />,
              { requiredPermission: "canUseJobs", feature: "shopRecords" }
            )
          } />

          <Route exact path={APP_ROUTE_PATHS.setupSheets} render={() =>
            renderProtectedPage(
              <SetupSheets user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} canApprove={activePermissions.canApproveRecords} />,
              { requiredPermission: "canUseJobs", feature: "shopRecords" }
            )
          } />

          <Route exact path={APP_ROUTE_PATHS.jobTravelers} render={() =>
            renderProtectedPage(
              <JobTravelers user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} canApprove={activePermissions.canApproveRecords} />,
              { requiredPermission: "canUseJobs", feature: "shopRecords" }
            )
          } />

          <Route exact path={APP_ROUTE_PATHS.inspection} render={() =>
            renderProtectedPage(
              <InspectionSheets user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} canApprove={activePermissions.canApproveRecords} />,
              { requiredPermission: "canUseJobs", feature: "shopRecords" }
            )
          } />

          <Route exact path={APP_ROUTE_PATHS.firstArticleInspections} render={() =>
            renderProtectedPage(
              <FirstArticleInspections user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} canApprove={activePermissions.canApproveRecords} />,
              { requiredPermission: "canUseJobs", feature: "shopRecords" }
            )
          } />

          <Route exact path={APP_ROUTE_PATHS.controlPlans} render={() =>
            renderProtectedPage(
              <ControlPlans user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} canApprove={activePermissions.canApproveRecords} />,
              { requiredPermission: "canUseJobs", feature: "shopRecords" }
            )
          } />

          <Route exact path={APP_ROUTE_PATHS.shopBrain} render={() =>
            renderProtectedPage(
              <ShopBrain user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} canApprove={activePermissions.canApproveRecords} />,
              { requiredPermission: "canUseJobs", feature: "shopRecords" }
            )
          } />

          <Route exact path={APP_ROUTE_PATHS.capacityPlanner} render={() =>
            renderProtectedPage(<CapacityPlanner />, { requiredPermission: "canUseJobs", feature: "shopRecords" })
          } />

          <Route exact path={APP_ROUTE_PATHS.shopFloor} render={() =>
            renderProtectedPage(<ShopFloorMode />, { requiredPermission: "canUseJobs", feature: "shopRecords" })
          } />

          <Route exact path={APP_ROUTE_PATHS.aiAssistant} render={() =>
            renderProtectedPage(<AIAssistant accountProfile={accountProfile} />, { requiredPermission: "canUseCalculators", feature: "aiCopilot" })
          } />

          <Route exact path={APP_ROUTE_PATHS.qrCodes} render={() =>
            renderProtectedPage(<QRCodeCenter canExport={activePermissions.canExport} />, { requiredPermission: "canUseJobs", feature: "shopRecords" })
          } />

          <Route
            exact
            path={APP_ROUTE_PATHS.exports}
            render={() =>
              renderProtectedPage(<ExportCenter user={user} />, {
                requiredPermission: "canExport",
                feature: "advancedExports",
              })
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.threeS}
            render={() =>
              renderProtectedPage(<ThreeSPage user={user} />, {
                requiredPermission: "canUseCalculators",
                feature: "calculators",
              })
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.trelloConnect}
            render={() =>
              renderProtectedPage(<TrelloConnectPage user={user} />, {
                allowWithoutAccess: true,
              })
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.trelloEmbeddedApp}
            render={() =>
              renderEmbeddedPage(
                <TrelloEmbeddedApp
                  user={user}
                  accountProfile={accountProfile}
                  rolePermissions={activePermissions}
                  isPlatformOwner={isPlatformOwner}
                />,
                { allowWithoutAccess: true, allowWithoutUser: true }
              )
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.calculator}
            render={({ match }) =>
              renderProtectedPage(
                <Calculator
                  user={user}
                  preferences={preferences}
                  toolId={match.params.toolId}
                  rolePermissions={activePermissions}
                />,
                { requiredPermission: "canUseCalculators", feature: "calculators" }
              )
            }
          />

          <Route render={() => <Redirect to={APP_ROUTE_PATHS.home} />} />
            </Switch>
          </Suspense>
        </RouteErrorBoundary>
      </div>
    </Router>
  );
}

export default App;

